import React, { useState } from "react";
import {
    Box,
    TextField,
    Button,
    CircularProgress,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Grid,
    IconButton,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import axios from "axios";
import GDSNProductDetailsCard from "../../components/Cards/GDSNProductDetailsCard"; // Ensure correct path
import USDAExpertSidebar from "../global/USDAExpertSidebar"; // Import the USDAExpertSidebar
import figLogo from "../../assets/fig_CROP.png"; // FIG Logo

const GDSNProductSearch = () => {
    // Set default to light mode
    const lightTheme = createTheme({
        palette: {
            mode: "light",
            primary: { main: "#1976d2" },
            background: { default: "#f4f6f8", paper: "#ffffff" },
            text: { primary: "#000000", secondary: "#555555" },
        },
    });

    const [productName, setProductName] = useState("");
    const [manufacturer, setManufacturer] = useState("");
    const [gtin, setGtin] = useState("");
    const [upc, setUpc] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);

    const apiUrl = "https://api.swancreate.com/api/nbf/ProductMst"; // Replace with the actual API endpoint

    const handleSearch = async () => {
        if (!productName.trim() && !manufacturer.trim() && !gtin.trim() && !upc.trim()) {
            setError("Please enter at least one search criteria.");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const response = await axios.get(apiUrl);
            const data = response.data || [];

            const filteredResults = data.filter((item) => {
                let matches = true;

                if (productName.trim()) {
                    matches = matches && item.ProductName?.toLowerCase().includes(productName.toLowerCase());
                }

                if (manufacturer.trim()) {
                    matches = matches && item.ManufacturerProductCode?.toLowerCase().includes(manufacturer.toLowerCase());
                }

                if (gtin.trim()) {
                    matches = matches && item.GTIN?.toLowerCase().includes(gtin.toLowerCase());
                }

                if (upc.trim()) {
                    matches = matches && item.UPC?.toLowerCase().includes(upc.toLowerCase());
                }

                return matches;
            });

            setResults(filteredResults);
        } catch (err) {
            console.error("Error fetching data:", err);
            setError("Failed to fetch data. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    const handleRowClick = (product) => {
        setSelectedProduct(product);
    };

    const handleClose = () => {
        setSelectedProduct(null);
    };

    const handlePrint = () => {
        window.print();
    };

    return (
        <ThemeProvider theme={lightTheme}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    height: "100vh",
                    overflow: "hidden",
                }}
            >
                {/* Main Content Area */}
                <Box
                    sx={{
                        flex: isSidebarOpen ? 1 : 1.5,
                        padding: "20px",
                        backgroundColor: lightTheme.palette.background.default,
                        overflowY: "auto",
                    }}
                >
                    <IconButton
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                        sx={{
                            position: "absolute",
                            top: "16px",
                            right: "16px",
                            backgroundColor: lightTheme.palette.primary.main,
                            borderRadius: "50%",
                            width: "48px",
                            height: "48px",
                        }}
                    >
                        <img
                            src={figLogo}
                            alt="FIG Logo"
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "50%",
                            }}
                        />
                    </IconButton>

                    <Typography variant="h4" gutterBottom color={lightTheme.palette.text.primary}>
                        GDSN Product Search
                    </Typography>

                    <Paper
                        sx={{
                            padding: "20px",
                            marginBottom: "20px",
                            backgroundColor: lightTheme.palette.background.paper,
                        }}
                    >
                        <Typography variant="h6" gutterBottom color={lightTheme.palette.text.primary}>
                            How to Use This Page
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            Enter one or more of the following criteria to search for products:
                        </Typography>
                        <Typography
                            variant="body1"
                            gutterBottom
                            component="ul"
                            sx={{ textAlign: "left" }}
                        >
                            <li>Product Name</li>
                            <li>Manufacturer Code</li>
                            <li>GTIN</li>
                            <li>UPC</li>
                        </Typography>
                    </Paper>

                    <Box sx={{ marginBottom: "20px" }}>
                        <Grid container spacing={2} alignItems="center" justifyContent="center">
                            <Grid item>
                                <TextField
                                    label="Product Name"
                                    variant="outlined"
                                    value={productName}
                                    onChange={(e) => setProductName(e.target.value)}
                                    sx={{ minWidth: "200px" }}
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                    label="Manufacturer Code"
                                    variant="outlined"
                                    value={manufacturer}
                                    onChange={(e) => setManufacturer(e.target.value)}
                                    sx={{ minWidth: "200px" }}
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                    label="GTIN"
                                    variant="outlined"
                                    value={gtin}
                                    onChange={(e) => setGtin(e.target.value)}
                                    sx={{ minWidth: "200px" }}
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                    label="UPC"
                                    variant="outlined"
                                    value={upc}
                                    onChange={(e) => setUpc(e.target.value)}
                                    sx={{ minWidth: "200px" }}
                                />
                            </Grid>
                            <Grid item>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSearch}
                                    disabled={loading}
                                >
                                    {loading ? <CircularProgress size={24} /> : "Search"}
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>

                    {error && (
                        <Typography variant="body1" color="error" gutterBottom>
                            {error}
                        </Typography>
                    )}

                    {results.length > 0 && (
                        <TableContainer component={Paper} sx={{ marginTop: "20px" }}>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: lightTheme.palette.primary.main }}>
                                        <TableCell sx={{ color: lightTheme.palette.common.white }}>GTIN</TableCell>
                                        <TableCell sx={{ color: lightTheme.palette.common.white }}>UPC</TableCell>
                                        <TableCell sx={{ color: lightTheme.palette.common.white }}>Product Name</TableCell>
                                        <TableCell sx={{ color: lightTheme.palette.common.white }}>Brand</TableCell>
                                        <TableCell sx={{ color: lightTheme.palette.common.white }}>Description</TableCell>
                                        <TableCell sx={{ color: lightTheme.palette.common.white }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {results.map((product, index) => (
                                        <TableRow
                                            key={index}
                                            hover
                                            sx={{
                                                cursor: "pointer",
                                                backgroundColor:
                                                    index % 2 === 0
                                                        ? lightTheme.palette.action.hover
                                                        : "inherit",
                                            }}
                                        >
                                            <TableCell>{product.GTIN || "N/A"}</TableCell>
                                            <TableCell>{product.UPC || "N/A"}</TableCell>
                                            <TableCell>{product.ProductName || "N/A"}</TableCell>
                                            <TableCell>{product.BrandName || "N/A"}</TableCell>
                                            <TableCell>{product.Description || "N/A"}</TableCell>
                                            <TableCell>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => handleRowClick(product)} // Opens the product data component
                                                >
                                                    View Product Data
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                    )}

                    {selectedProduct && (
                        <GDSNProductDetailsCard
                            productData={selectedProduct}
                            onClose={handleClose}
                            onPrint={handlePrint}
                        />
                    )}
                </Box>

                {/* USDAExpertSidebar */}
                {isSidebarOpen && (
                    <Box
                        sx={{
                            width: "400px",
                            flexShrink: 0,
                            backgroundColor: lightTheme.palette.background.paper,
                            boxShadow: "-2px 0 4px rgba(0, 0, 0, 0.2)",
                        }}
                    >
                        <USDAExpertSidebar />
                    </Box>
                )}
            </Box>
        </ThemeProvider>
    );
};

export default GDSNProductSearch;
